import React from 'react';
import { GraphQLClient } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';
import {
  GetRelatedBlogsAndNewsByTagDocument,
  GetRelatedBlogsAndNewsByTagQuery
} from '../../../../codegenGenerated/optigraphql/generated';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import Loader from '../Loader';
import { getEnvGQLUrl } from '../../../helpers';
import 'swiper/css/bundle';

const useGetRelatedBlogAndNews = (endpoint: string, tag: string, currentPageId: number) => {
  const graphQLClient = new GraphQLClient(endpoint);

  const { isError, error, isFetching, data } = useQuery({
    queryKey: ['GetRelatedBlogAndNewsByTag', { tag: tag, currentPageId: currentPageId }],
    queryFn: async () =>
      graphQLClient.request(GetRelatedBlogsAndNewsByTagDocument, {
        tag: tag,
        currentPageId: currentPageId
      }) as GetRelatedBlogsAndNewsByTagQuery,
    refetchOnWindowFocus: false
  });

  return {
    status: isError ? 'error' : isFetching ? 'loading' : 'success',
    error: error as Error,
    results: data && data.ArticleDetailPage?.items ? data.ArticleDetailPage.items : []
  };
};

export type RelatedBlogAndNewsSliderProps = {
  title: string;
  tag: string;
  currentPageId: string;
  imgFallback: string;
  noBackground?: string;
};

export const RelatedBlogsAndNewsSlider = ({
  title,
  tag,
  currentPageId,
  imgFallback,
  noBackground
}: RelatedBlogAndNewsSliderProps) => {
  const { status, results } = useGetRelatedBlogAndNews(
    getEnvGQLUrl(),
    tag,
    parseInt(currentPageId)
  );

  if (status === 'loading') {
    return <Loader />;
  }

  return (
    <>
      {status === 'error' && <></>}
      {results.length === 0 && <></>}
      {results.length > 1 && (
        <>
          <div
            className={`module-header ${noBackground === 'true' ? 'module-header--no-background' : ''}`}
          >
            <div className="container">
              <h2 className="module-header__title">{title ? title : ''}</h2>
            </div>
          </div>
          <div className="container">
            <Swiper
              className={'related-blogs-and-news-slider__carousel'}
              modules={[Navigation, Pagination]}
              slidesPerView={1}
              spaceBetween={40}
              autoHeight={true}
              loop={true}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev'
              }}
              watchOverflow={true}
              pagination={{
                el: '.swiper-scrollbar',
                type: 'progressbar'
              }}
              breakpoints={{
                767: {
                  slidesPerView: 2,
                  spaceBetween: 40
                },
                1024: {
                  slidesPerView: 2.5,
                  spaceBetween: 40
                }
              }}
            >
              {results.map(result => {
                const dateString = result && result.Date ? result.Date : '';
                const date = new Date(dateString);

                const formattedDate = date.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                });
                return (
                  result && (
                    <SwiperSlide key={result._id}>
                      <a
                        href={(result.OverrideCardLink?.Href as string) ?? result.Url}
                        title={(result.OverrideCardLink?.Title as string) ?? result.Title}
                        target={(result.OverrideCardLink?.Target as string) ?? ''}
                        className="generic-image-card__link-container"
                      >
                        <article className="generic-image-card generic-image-card--news">
                          <div className="picture generic-image-card__inner-picture">
                            <img
                              className="resources-menu__news-card-image"
                              src={result?.BlogAndNewsImage?.Url ?? imgFallback}
                              alt="about-card-image"
                              loading="lazy"
                              width="208"
                              height="150"
                            />
                          </div>
                          ∑
                          <div className="generic-image-card__info-container">
                            <div className="generic-image-card__info-container-inner">
                              <h3 className="generic-image-card__title">
                                {result.Title ? result.Title : ''}
                              </h3>
                              <div className="generic-image-card__date-and-read-time">
                                <time className="generic-image-card__date" dateTime={formattedDate}>
                                  {formattedDate}
                                </time>
                                <span className="generic-image-card__read-time">
                                  {result.ReadTime ? result.ReadTime : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        </article>
                      </a>
                    </SwiperSlide>
                  )
                );
              })}
              <div className="related-blogs-and-news-slider__controls">
                <div className="related-blogs-and-news-slider__scrollbar-wrapper">
                  <div className="swiper-scrollbar"></div>
                </div>
                <div className="related-blogs-and-news-slider__buttons">
                  <div className="swiper-button-prev">
                    <svg className="related-blogs-and-news-slider__button-icon" role="presentation">
                      <use xlinkHref="#chevron-left"></use>
                    </svg>
                  </div>
                  <div className="swiper-button-next">
                    <svg className="related-blogs-and-news-slider__button-icon" role="presentation">
                      <use xlinkHref="#chevron-right"></use>
                    </svg>
                  </div>
                </div>
              </div>
            </Swiper>
          </div>
        </>
      )}
    </>
  );
};
